import { useState, useEffect, useCallback } from 'react';
import { gameBridge } from '@/services/GameBridge';
import { Button } from '@/components/ui/button';
import { cn, truncate } from '@/lib/utils';
import { getSubjectByName } from '@/data/subjects';
import type {
  ClozeBlank,
  FlashCard,
  GameMode,
  MCQOption,
  RemediationTarget,
} from '@/types/deck';

interface EncounterData {
  cardId: string;
  card: FlashCard;
  question: string;
  options: MCQOption[];
  subject: string;
  isRematch: boolean;
  mode: GameMode;
  blanks?: ClozeBlank[];
  hint?: string;
  explain?: string;
  scenario?: string;
  remediation?: RemediationTarget[];
}

interface EncounterUIProps {
  visible: boolean;
}

export function EncounterUI({ visible }: EncounterUIProps) {
  const [encounterData, setEncounterData] = useState<EncounterData | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [blankSelections, setBlankSelections] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState<{
    correct: boolean;
    xp: number;
    coins: number;
    mode: GameMode;
    blankResults?: { blankId: string; selectedOptionId?: string; correctOptionId: string }[];
  } | null>(null);

  // Subscribe to encounter events
  useEffect(() => {
    const unsubscribers = [
      gameBridge.subscribe('encounter-started', (data) => {
        setEncounterData(data);
        setSelectedOptionId(null);
        setBlankSelections({});
        setIsSubmitted(false);
        setShowResult(false);
        setResultData(null);
      }),
      gameBridge.subscribe('answer-submitted', (data) => {
        setResultData({
          correct: data.correct,
          xp: data.xp,
          coins: data.coins,
          mode: data.mode,
          blankResults: data.blankResults,
        });
        setIsSubmitted(true);
        setShowResult(true);
      }),
    ];

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, []);

  const handleSubmit = useCallback(() => {
    if (!encounterData) return;
    if (encounterData.mode === 'cloze') {
      const blanks = encounterData.blanks || [];
      const allSelected = blanks.every(blank => Boolean(blankSelections[blank.id]));
      if (!allSelected) return;
      gameBridge.emit('answer-selected', { blankSelections });
      return;
    }

    if (!selectedOptionId) return;
    gameBridge.emit('answer-selected', { optionId: selectedOptionId });
  }, [encounterData, blankSelections, selectedOptionId]);

  // Handle keyboard shortcuts (1-4 for options, Enter for submit)
  useEffect(() => {
    if (!visible || !encounterData || isSubmitted || encounterData.mode === 'cloze') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '1' && e.key <= '4') {
        const index = parseInt(e.key) - 1;
        if (index < encounterData.options.length) {
          setSelectedOptionId(encounterData.options[index].id);
        }
      } else if (e.key === 'Enter' && selectedOptionId) {
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, encounterData, selectedOptionId, isSubmitted, handleSubmit]);

  const handleQuitRun = useCallback(() => {
    gameBridge.emit('return-to-hub', {});
  }, []);

  if (!visible || !encounterData) {
    return null;
  }

  const { card, question, options, subject, isRematch, mode, blanks = [], remediation = [] } = encounterData;
  const subjectInfo = getSubjectByName(subject);
  const subjectColor = subjectInfo?.color || '#4488ff';
  const blankResultMap = new Map(
    (resultData?.blankResults || []).map(result => [result.blankId, result])
  );
  const canSubmit =
    mode === 'cloze'
      ? blanks.every(blank => Boolean(blankSelections[blank.id]))
      : Boolean(selectedOptionId);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center pointer-events-none">
      <div className="game-panel max-w-[640px] w-full mx-4 pointer-events-auto animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="px-3 py-1 bg-secondary/50 rounded-lg border border-border/40">
              <span className="text-xs font-mono text-muted-foreground">
                #{card.id.slice(-4)}
              </span>
            </div>
            <div className="h-4 w-px bg-border/60" />
            <span
              className="text-sm font-semibold px-3 py-1 rounded-lg"
              style={{ 
                color: subjectColor,
                backgroundColor: `${subjectColor}20`,
                borderColor: `${subjectColor}40`,
                border: '1px solid'
              }}
            >
              {subject}
            </span>
          </div>
          {isRematch && (
            <div className="flex items-center gap-2 text-xs font-bold text-game-wrong px-3 py-1.5 bg-game-wrong/20 rounded-lg border border-game-wrong/40 animate-pulse">
              <span>⚔️</span>
              <span>REMATCH</span>
            </div>
          )}
        </div>

        {/* Topic */}
        <div className="mb-4">
          <h2 className="text-xl font-bold text-primary leading-tight">
            {card.topic}
          </h2>
        </div>

        {/* Question */}
        <div className="mb-6 p-4 bg-secondary/30 rounded-xl border border-border/40 space-y-2">
          {encounterData.scenario && (
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {encounterData.scenario}
            </p>
          )}
          <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
            {question}
          </p>
          {encounterData.hint && !showResult && (
            <p className="text-xs text-muted-foreground leading-relaxed">
              Hint: {encounterData.hint}
            </p>
          )}
        </div>

        {/* Options */}
        {mode === 'cloze' && blanks.length > 0 ? (
          <div className="space-y-4 mb-6">
            {blanks.map((blank, _blankIndex) => {
              const selectionResult = blankResultMap.get(blank.id);
              return (
                <div key={blank.id} className="p-4 rounded-xl border border-border/40 bg-secondary/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold px-2 py-1 rounded bg-secondary/60 text-muted-foreground">
                        {blank.label}
                      </span>
                      <span className="text-xs text-muted-foreground/80">
                        placeholder {blank.display}
                      </span>
                    </div>
                    {selectionResult && (
                      <span className={cn(
                        "text-xs font-semibold",
                        selectionResult.selectedOptionId === selectionResult.correctOptionId
                          ? "text-game-correct"
                          : "text-game-wrong"
                      )}>
                        {selectionResult.selectedOptionId === selectionResult.correctOptionId ? 'Correct' : 'Check again'}
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {blank.options.map((option, index) => {
                      const isSelected = blankSelections[blank.id] === option.id;
                      const showCorrect = showResult && option.id === blank.correctOptionId;
                      const showWrong = showResult && isSelected && option.id !== blank.correctOptionId;

                      return (
                        <button
                          key={option.id}
                          onClick={() => !isSubmitted && setBlankSelections(prev => ({ ...prev, [blank.id]: option.id }))}
                          disabled={isSubmitted}
                          className={cn(
                            "option-button flex items-start gap-3",
                            isSelected && !showResult && "selected",
                            showCorrect && "correct",
                            showWrong && "wrong"
                          )}
                        >
                          <span className={cn(
                            "flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-all",
                            isSelected && !showResult ? "bg-accent text-white scale-110" :
                            showCorrect ? "bg-game-correct text-white scale-110" :
                            showWrong ? "bg-game-wrong text-white animate-shake" :
                            "bg-secondary/80 text-secondary-foreground"
                          )}>
                            {String.fromCharCode(65 + index)}
                          </span>

                          <span className="text-sm text-left flex-1 leading-relaxed">
                            {truncate(option.text, 160)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3 mb-6">
            {options.map((option, index) => {
              const isSelected = selectedOptionId === option.id;
              const isCorrect = option.isCorrect;
              const showCorrect = showResult && isCorrect;
              const showWrong = showResult && isSelected && !isCorrect;

              return (
                <button
                  key={option.id}
                  onClick={() => !isSubmitted && setSelectedOptionId(option.id)}
                  disabled={isSubmitted}
                  className={cn(
                    "option-button flex items-start gap-4",
                    isSelected && !showResult && "selected",
                    showCorrect && "correct",
                    showWrong && "wrong"
                  )}
                >
                  {/* Option letter */}
                  <span className={cn(
                    "flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-all",
                    isSelected && !showResult ? "bg-accent text-white scale-110" :
                    showCorrect ? "bg-game-correct text-white scale-110" :
                    showWrong ? "bg-game-wrong text-white animate-shake" :
                    "bg-secondary/80 text-secondary-foreground"
                  )}>
                    {String.fromCharCode(65 + index)}
                  </span>

                  {/* Option text */}
                  <span className="text-sm text-left flex-1 leading-relaxed">
                    {truncate(option.text, 160)}
                  </span>

                  {/* Keyboard hint */}
                  {!isSubmitted && (
                    <span className="flex-shrink-0 text-xs text-muted-foreground/60 font-mono px-2 py-1 bg-secondary/50 rounded">
                      {index + 1}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Result message */}
        {showResult && resultData && (
          <div className={cn(
            "mb-6 p-4 rounded-xl text-center animate-slide-up border-2",
            resultData.correct 
              ? "bg-game-correct/20 border-game-correct/40" 
              : "bg-game-wrong/20 border-game-wrong/40"
          )}>
            <p className={cn(
              "text-xl font-bold mb-2",
              resultData.correct ? "text-game-correct" : "text-game-wrong"
            )}>
              {resultData.correct ? '✓ Correct!' : '✗ Incorrect'}
            </p>
            {resultData.correct && resultData.xp > 0 && (
              <div className="flex items-center justify-center gap-4 text-sm">
                <div className="flex items-center gap-1 text-blue-400 font-semibold">
                  <span className="animate-xp-pop">+{resultData.xp}</span>
                  <span className="text-xs opacity-80">XP</span>
                </div>
                <div className="flex items-center gap-1 text-yellow-400 font-semibold">
                  <span className="animate-xp-pop">+{resultData.coins}</span>
                  <span className="text-xs opacity-80">coins</span>
                </div>
              </div>
            )}
          </div>
        )}

        {showResult && !resultData?.correct && encounterData.explain && (
          <div className="mb-4 p-4 rounded-xl bg-secondary/30 border border-border/50 text-sm text-muted-foreground">
            {truncate(encounterData.explain, 220)}
          </div>
        )}

        {showResult && !resultData?.correct && remediation.length > 0 && (
          <div className="mb-4 p-4 rounded-xl bg-secondary/20 border border-border/50">
            <p className="text-sm font-semibold text-foreground mb-2">Likely confused with:</p>
            <div className="flex flex-wrap gap-2">
              {remediation.map(item => (
                <span
                  key={item.id}
                  className="text-xs px-3 py-1 rounded-full bg-secondary/60 border border-border/50 text-muted-foreground"
                >
                  {item.topic} {item.subject ? `(${item.subject})` : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-border/40">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleQuitRun}
            className="text-muted-foreground hover:text-foreground"
          >
            Quit Run
          </Button>

          {!isSubmitted ? (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              variant="default"
              size="lg"
              className="min-w-32"
            >
              {mode === 'cloze' ? 'Submit Blanks' : 'Submit Answer'}
            </Button>
          ) : (
            <div className="text-sm text-muted-foreground flex items-center gap-2" role="status" aria-label="Processing answer">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              {resultData?.correct ? 'Continuing...' : 'Reviewing...'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
